
import { useState, useEffect, lazy, Suspense } from 'react';
import { SEO } from '@/components/SEO';
import { useAuth } from '@/hooks/useAuth';
import { useFeaturedTutors } from '@/hooks/useFeaturedTutors';
import { useSubjects } from '@/hooks/useSubjects';
import { useQuery } from '@tanstack/react-query';
import { useSavedTutors } from '@/hooks/useSavedTutors';
import { useBatchReviews } from '@/hooks/useBatchReviews';
import { useToast } from '@/hooks/use-toast';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Star, Heart, Clock, Globe, Video, Search, Filter, Users, BookOpen, Zap, Award, TrendingUp, Target, BarChart3, CheckCircle, ArrowRight, Play, Calendar, DollarSign, MapPin, MessageCircle, Share, ChevronDown, Trophy, Wallet, Info, Download } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useNavigate } from 'react-router-dom';
import { pb } from '@/lib/pocketbase';
import { getUserInitials } from '@/utils/profileUtils';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { PostInputSection } from '@/components/PostInputSection';
import BookingModal from '@/components/BookingModal';
import SendMessageModal from '@/components/SendMessageModal';
import SubjectsModal from '@/components/SubjectsModal';

import TutorCountryFlag from '@/components/TutorCountryFlag';
import { translateSearchQuery } from '@/utils/subjectMapping';
import SubjectsSidebar from '@/components/SubjectsSidebar';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import { useImagePreloader } from '@/hooks/useImagePreloader';
import PaymentMethodsModal from '@/components/PaymentMethodsModal';
import TutorPricingModal from '@/components/TutorPricingModal';
import { currencies } from '@/contexts/CurrencyContext';
const PromoBanner = lazy(() => import('@/components/PromoBanner'));
import HomeTravelStories from '@/components/HomeTravelStories';
import { useActiveDiscount } from '@/hooks/useActiveDiscount';
import { LearnFilterCard } from '@/components/LearnFilterCard';
import { useHomepageSectionSettings } from '@/hooks/useHomepageSectionSettings';

// Lazy load heavy components
const InspirePostsSlider = lazy(() => import('@/components/InspirePostsSlider').then(m => ({ default: m.InspirePostsSlider })));
const SuccessStoriesTicker = lazy(() => import('@/components/fitness-chat/SuccessStoriesTicker'));
const SuccessStoryForm = lazy(() => import('@/components/SuccessStoryForm'));
const AboutCarousel = lazy(() => import('@/components/home/AboutCarousel'));
const HomepageCarousel = lazy(() => import('@/components/homepage/HomepageCarousel'));

// Optimized Rating Component - receives pre-fetched data
const TutorRating = ({ rating, count }: { rating: number; count: number }) => {
  return (
    <div className="flex items-center gap-1">
      <Star className="h-3 w-3 text-yellow-600 fill-yellow-600" />
      <span className="text-xs text-yellow-800 font-medium">
        {rating.toFixed(1)} ({count})
      </span>
    </div>
  );
};

const Home = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { featuredTutors, isLoading } = useFeaturedTutors();
  const { subjects } = useSubjects();
  const { toggleSavedTutor, isTutorSaved } = useSavedTutors();
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  
  // Batch fetch reviews for all featured tutors
  const tutorIds = featuredTutors.map(t => t.id);
  const { reviewStats } = useBatchReviews(tutorIds);
  const { formatPrice, selectedCurrency, currencies: contextCurrencies } = useCurrencyFormat();
  const { activeDiscount, appliesToType } = useActiveDiscount();
  const { settings: homepageSectionSettings, slides: carouselSlides, isLoading: isLoadingSectionSettings } = useHomepageSectionSettings();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTutorForBooking, setSelectedTutorForBooking] = useState<any>(null);
  const [selectedTutorForMessage, setSelectedTutorForMessage] = useState<any>(null);
  const [triggerSuccessStoryModal, setTriggerSuccessStoryModal] = useState(false);
  const [isSubjectsModalOpen, setIsSubjectsModalOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedTutorForPayment, setSelectedTutorForPayment] = useState<any>(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedTutorForPricing, setSelectedTutorForPricing] = useState<any>(null);

  // Helper function to convert tutor's price to selected currency
  const formatTutorPrice = (tutor: any) => {
    const price = tutor.hourlyRate || 0;
    if (!price) return '0';
    
    const tutorCurrency = tutor.pricingCurrency || 'USD';
    const tutorCurrencyData = contextCurrencies.find(c => c.code === tutorCurrency);
    
    // Convert tutor's price to USD first
    let priceInUSD = price;
    if (tutorCurrency !== 'USD' && tutorCurrencyData) {
      priceInUSD = price / tutorCurrencyData.rate;
    }
    
    // Use the context's formatPrice to ensure consistent conversion and rounding
    const formatted = formatPrice(priceInUSD);
    
    // For EGP, convert to Arabic numerals
    if (selectedCurrency.code === 'EGP') {
      return formatted.replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    }
    
    return formatted;
  };

  // Helper function to get localized text based on selected currency
  const getLocalizedText = (text: string) => {
    if (selectedCurrency.code === 'EGP') {
      switch (text) {
        case 'per hour':
          return 'في الساعة';
        default:
          return text;
      }
    }
    return text;
  };

  // Fetch hero settings with optimized caching
  const { data: heroSettings, isLoading: heroSettingsLoading } = useQuery({
    queryKey: ['hero-settings'],
    queryFn: async () => {
      try {
        const settings = await pb.collection('hero_settings').getList(1, 1, {
          sort: '-updated'
        });
        if (settings.items.length > 0) {
          const result = {
            selectedTutorId: settings.items[0].selectedTutorId || '',
            selectedTutorImage: settings.items[0].selectedTutorImage || '',
            customImageUrl: settings.items[0].customImageUrl || '',
            useDefault: settings.items[0].useDefault !== false,
            leftImageUrl: settings.items[0].leftImageUrl || '',
            rightImageUrl: settings.items[0].rightImageUrl || '',
            leftImageTutorId: settings.items[0].leftImageTutorId || '',
            rightImageTutorId: settings.items[0].rightImageTutorId || '',
            quickNavBackgroundUrl: settings.items[0].quickNavBackgroundUrl || '',
            quickNavTutorId: settings.items[0].quickNavTutorId || '',
            useDefaultQuickNav: settings.items[0].useDefaultQuickNav !== false,
          };
          return result;
        }
        return { 
          selectedTutorId: '',
          selectedTutorImage: '', 
          customImageUrl: '',
          useDefault: true,
          leftImageUrl: '',
          rightImageUrl: '',
          leftImageTutorId: '',
          rightImageTutorId: '',
          quickNavBackgroundUrl: '',
          quickNavTutorId: '',
          useDefaultQuickNav: true,
        };
      } catch (error) {
        console.error('Error fetching hero settings:', error);
        return { 
          selectedTutorId: '',
          selectedTutorImage: '', 
          customImageUrl: '',
          useDefault: true,
          leftImageUrl: '',
          rightImageUrl: '',
          leftImageTutorId: '',
          rightImageTutorId: '',
          quickNavBackgroundUrl: '',
          quickNavTutorId: '',
          useDefaultQuickNav: true,
        };
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 300000, // Cache for 5 minutes
  });

  // Fetch selected tutor details for hero button
  const { data: selectedTutor } = useQuery({
    queryKey: ['selected-tutor', heroSettings?.selectedTutorId],
    queryFn: async () => {
      if (!heroSettings?.selectedTutorId) return null;
      try {
        return await pb.collection('tutors').getOne(heroSettings.selectedTutorId, {
          expand: 'user'
        });
      } catch (error) {
        console.error('Error fetching selected tutor:', error);
        return null;
      }
    },
    enabled: !!heroSettings?.selectedTutorId && !heroSettings?.useDefault,
  });

  // Fetch left image tutor details
  const { data: leftImageTutor } = useQuery({
    queryKey: ['left-image-tutor', heroSettings?.leftImageTutorId],
    queryFn: async () => {
      if (!heroSettings?.leftImageTutorId) return null;
      try {
        return await pb.collection('tutors').getOne(heroSettings.leftImageTutorId, {
          expand: 'user'
        });
      } catch (error) {
        console.error('Error fetching left image tutor:', error);
        return null;
      }
    },
    enabled: !!heroSettings?.leftImageTutorId,
  });

  // Fetch right image tutor details
  const { data: rightImageTutor } = useQuery({
    queryKey: ['right-image-tutor', heroSettings?.rightImageTutorId],
    queryFn: async () => {
      if (!heroSettings?.rightImageTutorId) return null;
      try {
        return await pb.collection('tutors').getOne(heroSettings.rightImageTutorId, {
          expand: 'user'
        });
      } catch (error) {
        console.error('Error fetching right image tutor:', error);
        return null;
      }
    },
    enabled: !!heroSettings?.rightImageTutorId,
  });

  // Fetch Quick Navigation tutor details
  const { data: quickNavTutor } = useQuery({
    queryKey: ['quick-nav-tutor', heroSettings?.quickNavTutorId],
    queryFn: async () => {
      if (!heroSettings?.quickNavTutorId) return null;
      try {
        return await pb.collection('tutors').getOne(heroSettings.quickNavTutorId, {
          expand: 'user'
        });
      } catch (error) {
        console.error('Error fetching quick nav tutor:', error);
        return null;
      }
    },
    enabled: !!heroSettings?.quickNavTutorId,
  });
  

  // Generate subjects list dynamically from the database (remove duplicates)
  const subjectsList = ['All Subjects', ...Array.from(new Set(subjects.map(subject => subject.name)))];

  // Navigation sections for the home page
  const navigationSections = [
    {
      name: "Find Tutors",
      icon: <Search className="h-5 w-5" />,
      description: "Search for your perfect tutor",
      scrollTo: "search-section",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      name: "Subjects",
      icon: <BookOpen className="h-5 w-5" />,
      description: "Browse all available subjects",
      action: "modal",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      name: "How It Works",
      icon: <Target className="h-5 w-5" />,
      description: "Learn about our process",
      scrollTo: "how-it-works",
      gradient: "from-green-500 to-emerald-500"
    },
    {
      name: "Top Tutors",
      icon: <Star className="h-5 w-5" />,
      description: "Meet our featured experts",
      scrollTo: "featured-tutors",
      gradient: "from-orange-500 to-red-500"
    },
    {
      name: "Become a Tutor",
      icon: <Users className="h-5 w-5" />,
      description: "Start teaching today",
      scrollTo: "become-tutor",
      gradient: "from-teal-500 to-blue-500"
    }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleNavigationClick = (section: any) => {
    if (section.action === 'modal') {
      setIsSubjectsModalOpen(true);
    } else if (section.scrollTo) {
      scrollToSection(section.scrollTo);
    }
  };

  // Handle booking modal from auth redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const openBooking = urlParams.get('openBooking');
    
    if (openBooking === 'true' && user) {
      // Get stored tutor data
      const storedTutorData = localStorage.getItem('pendingBookingTutor');
      if (storedTutorData) {
        try {
          const tutorData = JSON.parse(storedTutorData);
          setSelectedTutorForBooking(tutorData);
          localStorage.removeItem('pendingBookingTutor');
          
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          // Error parsing stored tutor data - silently continue
        }
      }
    }
  }, [user]);

  // Handle return to create success story after login
  useEffect(() => {
    if (user) {
      const returnToAction = localStorage.getItem('returnToAction');
      if (returnToAction === 'create-success-story') {
        localStorage.removeItem('returnToAction');
        // Trigger the success story modal to open
        setTriggerSuccessStoryModal(true);
        // Reset the trigger after a brief moment
        setTimeout(() => setTriggerSuccessStoryModal(false), 100);
      }
    }
  }, [user]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery) {
      const translatedQuery = translateSearchQuery(searchQuery);
      params.append('search', translatedQuery);
    }
    if (selectedSubject && selectedSubject !== 'All Subjects') params.append('subject', selectedSubject);
    
    const queryString = params.toString();
    const targetUrl = `/tutors${queryString ? `?${queryString}` : ''}`;
    navigate(targetUrl);
  };

  const getTutorName = (tutor: any) => {
    // First try the tutor's own name field from the tutor database
    if (tutor.name && tutor.name.trim()) {
      return tutor.name;
    }
    // Fallback to the expanded user name
    if (tutor.expand?.user?.name) {
      return tutor.expand.user.name;
    }
    return 'Unknown Tutor';
  };

  const handleBookSession = (tutor: any) => {
    setSelectedTutorForBooking(tutor);
  };

  const handleViewLessons = (tutor: any) => {
    navigate(`/tutor-lessons?tutorId=${tutor.id}`);
  };

  const handleTrialSession = (tutor: any) => {
    const roomId = `trial_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    navigate(`/classroom?role=student&bookingId=${roomId}`);
    
    toast({
      title: "Trial Session Started!",
      description: "Share the room link with your tutor to begin the session.",
      duration: 5000
    });
  };

  // Get tutor name for hero button
  const getSelectedTutorName = () => {
    let fullName = '';
    if (selectedTutor?.name) fullName = selectedTutor.name;
    else if (selectedTutor?.expand?.user?.name) fullName = selectedTutor.expand.user.name;
    else return 'This Tutor';
    
    // Extract first name only
    return fullName.split(' ')[0];
  };

  // Get URLs for the "Find the right tutor for you" section images with optimization
  const getLeftSectionImageUrl = (size: 'small' | 'medium' = 'medium') => {
    const baseUrl = heroSettings?.leftImageUrl || "https://t4.ftcdn.net/jpg/03/76/92/79/360_F_376927916_fEOIvubxQSXb9YbNgvvpkdkQsbUsMZ9r.jpg";
    const sizeParam = size === 'small' ? 'w=400&q=75' : 'w=600&q=80';
    
    // Don't add params to PocketBase URLs
    if (!baseUrl.includes('tutornesta.com') && !baseUrl.includes('pocketbase') && (baseUrl.includes('unsplash.com') || baseUrl.includes('ftcdn.net'))) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}${sizeParam}`;
    }
    return baseUrl;
  };

  const getRightSectionImageUrl = (size: 'small' | 'medium' = 'medium') => {
    const baseUrl = heroSettings?.rightImageUrl || "https://www.ziprecruiter.com/svc/fotomat/public-ziprecruiter/cms/1143696570InternationalOnlineTutor.jpg";
    const sizeParam = size === 'small' ? 'w=400&q=75' : 'w=600&q=80';
    
    // Don't add params to PocketBase URLs
    if (!baseUrl.includes('tutornesta.com') && !baseUrl.includes('pocketbase') && (baseUrl.includes('unsplash.com') || baseUrl.includes('ziprecruiter.com'))) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}${sizeParam}`;
    }
    return baseUrl;
  };

  // Get Quick Navigation background URL with optimization
  const getQuickNavBackgroundUrl = (size: 'small' | 'medium' | 'large' = 'medium') => {
    const sizeParams = {
      small: 'w=400&q=70',
      medium: 'w=800&q=75',
      large: 'w=1200&q=80'
    };
    
    let baseUrl = '';
    if (heroSettings?.useDefaultQuickNav) {
      baseUrl = "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b";
    } else if (heroSettings?.quickNavBackgroundUrl) {
      baseUrl = heroSettings.quickNavBackgroundUrl;
    } else {
      baseUrl = "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b";
    }
    
    // Only add optimization params for external CDN URLs, not PocketBase
    if (!baseUrl.includes('tutornesta.com') && !baseUrl.includes('pocketbase') && (baseUrl.includes('unsplash.com') || baseUrl.includes('images.'))) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}auto=format&fit=crop&${sizeParams[size]}`;
    }
    
    return baseUrl;
  };

  const getProfilePictureUrl = (tutor: any) => {
    if (tutor.profilePicture && tutor.profilePicture.trim()) {
      return pb.files.getURL(tutor, tutor.profilePicture);
    }
    
    if (tutor.profilePictureUrl && tutor.profilePictureUrl.trim()) {
      return tutor.profilePictureUrl;
    }
    
    return '';
  };

  const handleSendMessage = (tutor: any) => {
    setSelectedTutorForMessage(tutor);
  };

  const handleToggleSave = (tutor: any) => {
    toggleSavedTutor(tutor);
    toast({
      title: isTutorSaved(tutor.id) ? "Tutor Removed" : "Tutor Saved!",
      description: isTutorSaved(tutor.id) 
        ? "Tutor removed from your saved list" 
        : "Tutor added to your saved list",
      duration: 3000
    });
  };

  const handleTutorClick = (tutor: any) => {
    navigate(`/tutor/${tutor.id}`);
  };

  const handleShareTutor = async (tutor: any) => {
    const shareUrl = `https://tutornesta.com/tutor/${tutor.id}`;
    
    try {
      if (navigator.share && navigator.canShare) {
        const tutorName = getTutorName(tutor);
        const subjects = tutor.subjects?.slice(0, 3).join(', ') || 'Multiple subjects';
        const shareText = `Check out this amazing tutor: ${tutorName} - ${subjects} at $${tutor.hourlyRate || 0}/hr`;
        
        await navigator.share({
          title: `Tutor Recommendation: ${tutorName}`,
          text: shareText,
          url: shareUrl,
        });
      } else {
        throw new Error('Web Share API not available');
      }
    } catch (error) {
      // Fallback to clipboard - only copy the URL
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast({
          title: "Link Copied!",
          description: "Tutor profile link copied to clipboard.",
          duration: 3000
        });
      } catch (clipboardError) {
        toast({
          title: "Share Failed",
          description: "Unable to share or copy link. Please try again.",
          duration: 3000,
          variant: "destructive"
        });
      }
    }
  };

  // Session preference helper function
  const getSessionPreferenceInfo = (preference?: string) => {
    switch (preference) {
      case 'chat_only':
        return { 
          text: 'Chat & Messages Only', 
          icon: MessageCircle, 
          color: 'bg-blue-100 text-blue-800 border-blue-200' 
        };
      case 'video_only':
        return { 
          text: 'Video Sessions Only', 
          icon: Video, 
          color: 'bg-green-100 text-green-800 border-green-200' 
        };
      case 'both':
        return { 
          text: 'Chat & Video Sessions', 
          icon: Video, 
          color: 'bg-purple-100 text-purple-800 border-purple-200' 
        };
      default:
        return { 
          text: 'Chat & Video Sessions', 
          icon: Video, 
          color: 'bg-purple-100 text-purple-800 border-purple-200' 
        };
    }
  };

  // Default hero image URL - must match the preload in index.html to avoid render delay
  const DEFAULT_HERO_IMAGE = 'https://api.tutornesta.com/api/files/pbc_1417903265/3wtme5ye7r06582/inbound6901118893957123266_77sfq9uk17_4t3z00ace1.png';

  // Get hero image URL based on settings with optimization
  // Always returns a valid URL to prevent element render delay
  const getHeroImageUrl = (size: 'small' | 'medium' | 'large' = 'large') => {
    const sizeParams = {
      small: 'w=400&q=75',
      medium: 'w=800&q=80',
      large: 'w=1200&q=85'
    };
    
    // PocketBase thumb sizes for different display sizes
    const pbThumbSizes = {
      small: 'thumb=400x400',
      medium: 'thumb=600x600',
      large: 'thumb=800x800'
    };
    
    let baseUrl = '';
    
    // During loading or if using default, use the preloaded default image
    if (heroSettingsLoading || heroSettings?.useDefault) {
      baseUrl = DEFAULT_HERO_IMAGE;
    } else if (heroSettings?.customImageUrl) {
      baseUrl = heroSettings.customImageUrl;
    } else if (heroSettings?.selectedTutorImage) {
      baseUrl = heroSettings.selectedTutorImage;
    } else {
      baseUrl = DEFAULT_HERO_IMAGE;
    }
    
    // Add PocketBase thumb optimization for tutornesta/pocketbase URLs
    if (baseUrl.includes('tutornesta.com') || baseUrl.includes('pocketbase')) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}${pbThumbSizes[size]}`;
    }
    
    // Add optimization params for Unsplash and other image services
    if (baseUrl.includes('unsplash.com') || baseUrl.includes('images.')) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}auto=format&fit=crop&${sizeParams[size]}`;
    }
    
    return baseUrl;
  };

  // Simple loading state
  const isLoadingHero = heroSettingsLoading;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50/50 overflow-x-hidden w-full">
      <SEO 
        title="TutorNesta - تيوترنيستا - Find Expert Online Tutors | مدرسين خصوصيين"
        description="Connect with qualified tutors worldwide for personalized online lessons | تواصل مع مدرسين مؤهلين في مصر - رياضيات، علوم، لغات، برمجة، موسيقى واللياقة البدنية. احجز درسك التجريبي اليوم."
        canonicalUrl="https://tutornesta.com/"
        keywords="online tutors, private tutor, مدرس خصوصي, مدرسين اونلاين, دروس خصوصية في مصر, تعليم اونلاين مصر, توتورنيستا, توترنيستا"
      />
      {/* Subjects Sidebar */}
      <SubjectsSidebar />
      
      <Navbar />
      
      {/* Mobile Join Us / Welcome Button with Learn Filter */}
      <div className="block lg:hidden mt-16 mb-4 px-4 py-2">
        <div className="flex items-center gap-3">
          {/* Learn Filter Card - Square */}
          <LearnFilterCard />
          
          {/* Welcome/Get Started Text */}
          <div className="flex-1 text-center">
            {user ? (
              <span 
                onClick={() => navigate('/profile')}
                className="text-purple-600 font-semibold text-xs cursor-pointer hover:underline inline-flex items-center justify-center gap-1"
              >
                Welcome, {user.name || user.email?.split('@')[0]}
              </span>
            ) : (
              <span 
                onClick={() => navigate('/auth')}
                className="text-purple-600 font-semibold text-xs cursor-pointer hover:underline inline-flex items-center justify-center gap-1 group"
              >
                Sign up free - سجّل مجاناً <ArrowRight className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            )}
            {/* Reserved space for discount to prevent layout shift */}
            <div className="mt-1 min-h-[18px]">
              {activeDiscount && appliesToType('bookings') && (
                <span className="text-red-500 font-bold text-[10px] animate-pulse" dir="rtl">
                  🔥 خصم {activeDiscount.percentage}% - {activeDiscount.announcement_text}
                </span>
              )}
            </div>
          </div>
          
          {/* Tutors Button */}
          <div 
            onClick={() => navigate('/tutors')}
            className="relative flex flex-col items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-50 to-pink-50 cursor-pointer transition-all duration-300 hover:shadow-lg overflow-hidden rounded-lg group border-2 border-purple-300 hover:border-purple-500 hover:scale-105 shrink-0"
          >
            <Users className="h-4 w-4 text-purple-600 mb-1 group-hover:scale-110 transition-transform" />
            <span className="text-[8px] font-semibold text-purple-700 text-center leading-tight">Our Tutors</span>
          </div>
        </div>
      </div>

      {/* Promotional Banner */}
      <div className="lg:mt-12">
        <Suspense fallback={<div className="h-64 bg-muted/20 animate-pulse rounded-lg" />}>
          <PromoBanner />
        </Suspense>
      </div>

      {/* Welcome Section */}
      <div className={`bg-purple-500/10 pt-0 lg:pt-8 pb-2 sm:pb-3 mt-2 lg:mt-20 w-full lg:w-[80%] ml-0 lg:ml-[20%] overflow-x-hidden`}>
        {/* Mobile Tabs View */}
        <div className="block lg:hidden">
          <Tabs defaultValue="platform" className="w-full">
            <TabsList className="w-full grid grid-cols-4 bg-white/80 !rounded-none h-11 p-1 gap-1 shadow-sm border border-gray-100">
              <TabsTrigger value="platform" className="text-[10px] sm:text-xs font-semibold !rounded-none transition-all duration-300 data-[state=active]:bg-sky-100 data-[state=active]:text-sky-700 data-[state=active]:shadow-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50">
                من نحن
              </TabsTrigger>
              <TabsTrigger value="tutors" className="text-[10px] sm:text-xs font-semibold !rounded-none transition-all duration-300 data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700 data-[state=active]:shadow-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50">
                المدرسين
              </TabsTrigger>
              <TabsTrigger value="courses" className="text-[10px] sm:text-xs font-semibold !rounded-none transition-all duration-300 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-700 data-[state=active]:shadow-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50">
                الكورسات
              </TabsTrigger>
              <TabsTrigger value="gallery" className="text-[10px] sm:text-xs font-semibold !rounded-none transition-all duration-300 data-[state=active]:bg-rose-100 data-[state=active]:text-rose-700 data-[state=active]:shadow-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50">
                المعرض
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="platform" className="mt-0 p-3 pt-3 h-[90px] overflow-hidden">
              <Suspense fallback={
                <div className="h-[90px] flex items-center justify-center">
                  <div className="animate-pulse text-gray-400 text-xs">جاري التحميل...</div>
                </div>
              }>
                <AboutCarousel />
              </Suspense>
            </TabsContent>
            
            <TabsContent value="tutors" className="mt-0 p-2 h-[90px] overflow-hidden">
              <div className="text-center h-full flex flex-col justify-center gap-1">
                <div className="flex flex-wrap justify-center gap-2 mt-3">
                  <Button onClick={() => navigate('/inspire-posts')} className="bg-emerald-100 hover:bg-emerald-200 text-emerald-700 px-4 py-1.5 text-[11px] font-bold h-auto shadow-sm" style={{ borderRadius: 0 }}>
                    مشاركات المدرسين
                  </Button>
                  <Button onClick={() => navigate('/tutor-videos')} className="bg-emerald-100 hover:bg-emerald-200 text-emerald-700 px-4 py-1.5 text-[11px] font-bold h-auto shadow-sm" style={{ borderRadius: 0 }}>
                    فيديوهات المدرسين
                  </Button>
                </div>
                <div className="flex justify-center gap-2 mt-1">
                  <Button onClick={() => navigate('/subjects/english')} className="bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1 text-[10px] font-medium h-auto" style={{ borderRadius: 0 }}>
                    English
                  </Button>
                  <Button onClick={() => navigate('/subjects/fitness')} className="bg-green-50 hover:bg-green-100 text-green-600 px-3 py-1 text-[10px] font-medium h-auto" style={{ borderRadius: 0 }}>
                    Fitness
                  </Button>
                  <Button onClick={() => navigate('/subjects/programming')} className="bg-purple-50 hover:bg-purple-100 text-purple-600 px-3 py-1 text-[10px] font-medium h-auto" style={{ borderRadius: 0 }}>
                    Programming
                  </Button>
                  <Button onClick={() => navigate('/subjects/music')} className="bg-pink-50 hover:bg-pink-100 text-pink-600 px-3 py-1 text-[10px] font-medium h-auto" style={{ borderRadius: 0 }}>
                    Music
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="courses" className="mt-0 p-3 h-[90px] overflow-hidden">
              <div className="text-center h-full flex flex-col justify-center">
                <div className="flex flex-wrap justify-center gap-2">
                  <Button onClick={() => navigate('/explore-lessons')} className="bg-amber-100 hover:bg-amber-200 text-amber-700 px-3 py-1 text-[10px] font-bold h-auto shadow-sm" style={{ borderRadius: 0 }}>
                    دروس مجانيه
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="gallery" className="mt-0 p-3 h-[90px] overflow-hidden">
              <div className="text-center h-full flex flex-col justify-center">
                <div className="flex flex-wrap justify-center gap-2">
                  <Button onClick={() => navigate('/travel-stories')} className="bg-rose-100 hover:bg-rose-200 text-rose-700 px-3 py-1 text-[10px] font-bold h-auto shadow-sm" style={{ borderRadius: 0 }}>
                    View Stories
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          {/* Search Input - Mobile */}
          <div className="mt-4 flex justify-center px-4 pb-2">
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                const searchInput = (e.target as HTMLFormElement).querySelector('input') as HTMLInputElement;
                if (searchInput?.value.trim()) {
                  const translatedQuery = translateSearchQuery(searchInput.value.trim());
                  navigate(`/tutors?search=${encodeURIComponent(translatedQuery)}`);
                }
              }}
              className="w-full max-w-xl relative"
            >
              <Input
                type="text"
                placeholder="Search for tutors or subjects - ابدأ البحث"
                className="w-full pl-12 pr-4 py-3 rounded-full border-2 border-gray-200 focus:border-purple-500 transition-all"
                style={{ fontSize: '10px' }}
              />
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Button 
                type="submit"
                size="icon"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 rounded-full h-9 w-9 bg-purple-600 hover:bg-purple-700"
              >
                <Search className="h-4 w-4 text-white" />
              </Button>
            </form>
          </div>
        </div>
        
        {/* Desktop View - Original Layout */}
        <div className="hidden lg:block">
          <div className="w-full px-2 sm:px-4 lg:px-4">
            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-1 sm:gap-2 lg:gap-3">
              <div className="flex flex-col gap-1 sm:gap-2">
                <Button
                  onClick={() => navigate('/explore-lessons')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-2 sm:px-3 py-1 lg:px-4 lg:py-3 xl:px-6 xl:py-4 rounded text-[9px] sm:text-xs lg:text-base xl:text-lg font-bold h-auto"
                >
                  دروس مجانيه
                </Button>
                <Button
                  onClick={() => navigate('/featured')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-2 sm:px-3 py-1 lg:px-4 lg:py-3 xl:px-6 xl:py-4 rounded text-[9px] sm:text-xs lg:text-base xl:text-lg font-bold h-auto"
                >
                  أقسام أخرى
                </Button>
              </div>
              
              <div className="flex-1 text-center px-1 sm:px-2 min-w-0">
                <h2 className="text-sm sm:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 mb-0.5 sm:mb-1 truncate" style={{ unicodeBidi: 'plaintext' }}>
                  مرحبا بك فى منصه TutorNesta
                </h2>
                <p className="text-[10px] sm:text-sm lg:text-sm xl:text-base text-gray-700 truncate" style={{ unicodeBidi: 'plaintext' }}>
                  ابدأ معنا رحلتك التعليمية الممتعة والمفيدة
                </p>
                {!isInstalled && (
                  <Button
                    onClick={async () => {
                      const installed = await promptInstall();
                      if (!installed && !isInstallable) {
                        navigate('/install');
                      }
                    }}
                    className="mt-1 sm:mt-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[8px] sm:text-[10px] lg:text-xs font-bold h-auto flex items-center gap-1 mx-auto shadow-md hover:shadow-lg transition-all duration-300"
                  >
                    <Download className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    حمّل التطبيق - Install App
                  </Button>
                )}
                {/* Reserved space for discount to prevent layout shift */}
                <div className="mt-1 min-h-[20px] lg:min-h-[24px] flex flex-col items-center justify-center">
                  {activeDiscount && appliesToType('bookings') && (
                    <p className="text-red-500 font-bold text-[9px] lg:text-sm animate-pulse text-center" dir="rtl">
                      🔥 خصم {activeDiscount.percentage}% - {activeDiscount.announcement_text}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col gap-1 sm:gap-2">
                <Button
                  onClick={() => navigate('/inspire-posts')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-2 sm:px-3 py-1 lg:px-4 lg:py-3 xl:px-6 xl:py-4 text-[9px] sm:text-xs lg:text-base xl:text-lg font-bold h-auto whitespace-nowrap"
                  style={{ borderRadius: 0 }}
                >
                  مشاركات المدرسين
                </Button>
                <Button
                  onClick={() => navigate('/tutor-videos')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-2 sm:px-3 py-1 lg:px-4 lg:py-3 xl:px-6 xl:py-4 text-[9px] sm:text-xs lg:text-base xl:text-lg font-bold h-auto whitespace-nowrap"
                  style={{ borderRadius: 0 }}
                >
                  فيديوهات المدرسين
                </Button>
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex justify-center px-4">
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                const searchInput = (e.target as HTMLFormElement).querySelector('input') as HTMLInputElement;
                if (searchInput?.value.trim()) {
                  const translatedQuery = translateSearchQuery(searchInput.value.trim());
                  navigate(`/tutors?search=${encodeURIComponent(translatedQuery)}`);
                }
              }}
              className="w-full max-w-xl md:max-w-2xl relative"
            >
              <Input
                type="text"
                placeholder="Search for tutors or subjects - ابدأ البحث"
                className="w-full pl-12 pr-4 py-3 rounded-full border-2 border-gray-200 focus:border-purple-500 transition-all"
                style={{ fontSize: '10px' }}
              />
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Button 
                type="submit"
                size="icon"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 rounded-full h-9 w-9 bg-purple-600 hover:bg-purple-700"
              >
                <Search className="h-4 w-4 text-white" />
              </Button>
            </form>
          </div>
        </div>
      </div>
      
      <div className="pb-20 lg:pb-0 ml-0 lg:ml-[20%] pt-8 lg:pt-12 transition-all duration-300"> {/* Always account for sidebar on large screens */}
      {/* Travel Stories Section */}
        <PostInputSection showBackground={false} />
        <HomeTravelStories />
      
      <div className="container mx-auto px-4 max-w-7xl">
        <Separator className="bg-gray-100 h-[2px]" />
      </div>
      
      {/* Hero Section - Conditional Carousel or Tutor Section */}
      {isLoadingSectionSettings ? (
        <section className="relative pt-12 pb-20 lg:pt-16 lg:pb-32 w-full cls-stable-hero" style={{ minHeight: '728px', height: '728px', contain: 'layout style paint size' }}>
          <div className="container mx-auto px-6 lg:px-12 text-center max-w-7xl flex items-center justify-center" style={{ minHeight: '600px', height: '600px' }}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </section>
      ) : homepageSectionSettings.sectionMode === 'carousel' && carouselSlides.length > 0 ? (
        <section className="relative pt-12 pb-20 lg:pt-16 lg:pb-32 w-full cls-stable-hero" style={{ minHeight: '728px', height: '728px', contain: 'layout style paint size' }}>
          <Suspense
            fallback={
              <div className="container mx-auto px-6 lg:px-12 text-center max-w-7xl flex items-center justify-center" style={{ minHeight: '600px', height: '600px' }}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            }
          >
            <HomepageCarousel slides={carouselSlides} className="w-full" />
          </Suspense>
        </section>
      ) : (
        <section className="relative pt-12 pb-20 lg:pt-16 lg:pb-32 w-full cls-stable-hero" style={{ minHeight: '728px', height: '728px', contain: 'layout style paint size' }}>
          <div className="container mx-auto px-6 lg:px-12 text-center max-w-7xl" style={{ minHeight: '600px', height: '600px' }}>
            <div className="max-w-6xl mx-auto" style={{ minHeight: '550px' }}>
              {isLoadingHero ? (
                <div className="flex justify-center py-12" style={{ height: '320px' }}>
                  <div className="text-gray-500">Loading...</div>
                </div>
              ) : (
                <div className="grid lg:grid-cols-2 gap-8 items-center" style={{ minHeight: '400px' }}>
                  {/* Text Content */}
                  <div className="text-center lg:text-left space-y-6 order-2 lg:order-1" style={{ minHeight: '200px' }}>
                    <div className="space-y-4">
                      <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900">
                        Learn with Expert Tutors
                      </h1>
                      <p className="text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0">
                        Find expert tutors and coaches for personalized 1-on-1 lessons tailored to your learning goals.
                      </p>
                    </div>
                      
                    <div className="space-y-4">
                      {!user ? (
                        <Button 
                          size="lg" 
                          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg text-lg w-full sm:w-auto"
                          onClick={() => navigate('/auth')}
                        >
                          Get Started Today
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      ) : (
                        <Button 
                          size="lg" 
                          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg text-lg w-full sm:w-auto"
                          onClick={() => navigate('/tutors')}
                        >
                          Find Your Perfect Tutor
                          <Search className="ml-2 h-5 w-5" />
                        </Button>
                      )}
                    </div>
                  </div>
                    
                  {/* Tutor Photo */}
                  <div className="flex justify-center lg:justify-end order-1 lg:order-2" style={{ minHeight: '320px' }}>
                    <div className="relative" style={{ width: '320px', height: '320px' }}>
                      <div className="w-80 h-80 sm:w-96 sm:h-96 lg:w-[350px] lg:h-[350px] xl:w-[450px] xl:h-[450px] rounded-2xl overflow-hidden shadow-lg" style={{ aspectRatio: '1/1' }}>
                        <img 
                          src={getHeroImageUrl('large')}
                          alt="Professional tutor teaching online | مدرس محترف يعلم أونلاين"
                          className="w-full h-full object-cover"
                          loading="eager"
                          width="320"
                          height="320"
                          fetchPriority="high"
                        />
                        
                        {/* Contact This Tutor Button - Top Right */}
                        {heroSettings?.selectedTutorId && !heroSettings?.useDefault && (
                          <div className="absolute top-4 right-4 z-10" style={{ width: '97px', height: '36px' }}>
                            <Button
                              size="sm"
                              className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow-lg cursor-pointer"
                              onClick={() => navigate(`/tutor/${heroSettings.selectedTutorId}`)}
                            >
                              <MessageCircle className="h-4 w-4 mr-2" />
                              {getSelectedTutorName()}
                            </Button>
                          </div>
                        )}
                        
                        {/* Find Tutor Button Overlay */}
                        {heroSettings && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Button
                              size="lg"
                              className="bg-white/90 hover:bg-white text-blue-600 font-semibold py-4 px-8 rounded-lg shadow-lg"
                              onClick={() => navigate('/tutors')}
                            >
                              <Search className="mr-2 h-5 w-5" />
                              Find Tutor
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Separator */}
      <div className="container mx-auto px-4 max-w-7xl">
        <Separator className="bg-gray-100 h-[2px]" />
      </div>

      {/* Inspire Posts Slider Section */}
      <section className="py-8 bg-gradient-to-b from-white to-gray-50/50 w-full overflow-hidden">
        <div className="container mx-auto px-4 max-w-7xl">
          <Suspense fallback={
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          }>
            <InspirePostsSlider />
          </Suspense>
        </div>
      </section>

      {/* Navigation Buttons Section */}
      <section className="py-12 bg-gradient-to-b from-white to-gray-50/50 w-full overflow-hidden">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Show loading skeleton while hero settings are loading */}
          {heroSettingsLoading ? (
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-gray-200 animate-pulse">
              <div className="py-8 px-6">
                <div className="text-center mb-8">
                  <div className="h-8 bg-gray-300 rounded w-48 mx-auto mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-64 mx-auto"></div>
                </div>
                <div className="flex flex-wrap gap-4 justify-center max-w-lg mx-auto">
                  {[...Array(5)].map((_, index) => (
                    <div key={index} className="flex flex-col items-center flex-shrink-0">
                      <div className="w-16 h-16 bg-gray-300 rounded-full mb-3"></div>
                      <div className="h-4 bg-gray-300 rounded w-16"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Card Container */
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
              {/* Background - Responsive */}
              {/* Small/phone screens - optimized background */}
              <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat md:hidden"
                style={{ 
                  backgroundImage: heroSettings?.quickNavBackgroundUrl && !heroSettings?.useDefaultQuickNav
                    ? `url(${heroSettings.quickNavBackgroundUrl})`
                    : `url(${getQuickNavBackgroundUrl('small')})`
                }}
              >
                <div className="absolute inset-0 bg-black/40"></div>
              </div>
              
              {/* Mid/large screens - full photo with color background */}
              <div className="hidden md:block absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700">
                {!heroSettings?.useDefaultQuickNav && heroSettings?.quickNavBackgroundUrl && (
                  <div className="absolute right-8 top-1/2 -translate-y-1/2 w-48 h-48 lg:w-56 lg:h-56">
                    <img
                      src={heroSettings.quickNavBackgroundUrl}
                      alt="Featured tutor"
                      className="w-full h-full rounded-full object-cover object-center border-4 border-white/20 shadow-2xl"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        console.error('Quick nav image failed to load:', heroSettings.quickNavBackgroundUrl);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/20"></div>
              </div>
              
              <div className="relative z-10 py-8 px-6">
                {/* Tutor Button - Top Right Corner */}
                {heroSettings?.quickNavTutorId && !heroSettings?.useDefaultQuickNav && quickNavTutor && (
                  <div className="absolute top-3 right-3 z-20">
                    <Button
                      size="sm"
                      className="bg-green-500/80 backdrop-blur-sm text-white font-medium py-1 px-2 rounded-md shadow-md hover:shadow-lg transform transition-all duration-300 hover:scale-105 border border-green-300/60 hover:bg-green-500/90 flex items-center gap-1"
                      onClick={() => navigate(`/tutor/${heroSettings.quickNavTutorId}`)}
                    >
                      <MessageCircle className="h-2.5 w-2.5" />
                      <span className="text-xs">{quickNavTutor.name?.split(' ')[0] || 'Tutor'}</span>
                    </Button>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-white mb-2">Quick Navigation</h2>
                  <p className="text-white/90">Jump to any section on this page</p>
                </div>
                
                <div className="flex flex-wrap gap-4 justify-center max-w-lg mx-auto">
                  {navigationSections.map((section, index) => {
                    return (
                      <div key={section.name} className="flex flex-col items-center flex-shrink-0">
                        <Button
                          onClick={() => handleNavigationClick(section)}
                          className={`
                            group relative overflow-hidden w-12 h-12 rounded-full text-white font-semibold mb-3
                            bg-gradient-to-r ${section.gradient} hover:shadow-lg transform transition-all duration-300
                            hover:scale-110 animate-fade-in p-0
                          `}
                          style={{ animationDelay: `${index * 0.1}s` }}
                        >
                          <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-full"></div>
                          <div className="relative flex items-center justify-center">
                            <div className="transform group-hover:rotate-12 transition-transform duration-300">
                              {section.icon}
                            </div>
                          </div>
                          
                          {/* Animated border effect */}
                          <div className="absolute inset-0 rounded-full border-2 border-white opacity-0 group-hover:opacity-30 transform scale-105 group-hover:scale-110 transition-all duration-300"></div>
                        </Button>
                        <span className="text-sm font-medium text-center whitespace-nowrap text-white drop-shadow-lg"
                          style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
                          {section.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Separator */}
      <div className="container mx-auto px-4 max-w-7xl">
        <Separator className="bg-gray-100 h-[2px]" />
      </div>

      {/* Search & Filter Section - Added animations */}
      <section id="search-section" className="py-16 bg-gradient-to-b from-white to-gray-50/50 w-full overflow-hidden">
        <div className="container mx-auto px-4 max-w-7xl">
          <Card className="max-w-5xl mx-auto shadow-lg animate-fade-in">
            <CardHeader className="text-center">
              <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900 break-words animate-fade-in" style={{animationDelay: '0.2s'}}>
                Find Your Perfect Tutor or Coach
              </CardTitle>
              <CardDescription className="text-base sm:text-lg text-gray-600 break-words animate-fade-in" style={{animationDelay: '0.4s'}}>
                Search by tutor or subject
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="md:col-span-2 w-full min-w-0 animate-fade-in" style={{animationDelay: '0.6s'}}>
                  <Input
                    placeholder="ابحث عن مدرسين أو مواد - Search for tutors or subjects"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-12 text-xs sm:text-sm md:text-base w-full transform transition-all duration-300 hover:scale-105 focus:scale-105"
                  />
                </div>
                
                <div className="w-full min-w-0 animate-fade-in" style={{animationDelay: '0.8s'}}>
                  <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                    <SelectTrigger className="h-12 w-full transform transition-all duration-300 hover:scale-105 focus:scale-105">
                      <SelectValue placeholder="Subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjectsList.map((subject, index) => (
                        <SelectItem key={`${subject}-${index}`} value={subject}>{subject}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="text-center animate-fade-in" style={{animationDelay: '1s'}}>
                <Button 
                  size="lg" 
                  onClick={handleSearch}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-3 rounded-full w-full sm:w-auto transform transition-all duration-300 hover:scale-105 hover:shadow-lg"
                >
                  <Search className="mr-2 h-5 w-5" />
                  Search Tutors
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Separator */}
      <div className="container mx-auto px-4 max-w-7xl">
        <Separator className="bg-gray-100 h-[2px]" />
      </div>

      {/* Success Stories Section - Redesigned */}
      <section id="success-stories-section" className="py-16 bg-gradient-to-b from-white to-gray-50/50 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-20 h-20 bg-emerald-200 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 right-20 w-32 h-32 bg-teal-200 rounded-full blur-2xl"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-blue-200 rounded-full blur-lg"></div>
        </div>
        
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          {/* Enhanced Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Trophy className="h-4 w-4" />
              Success Stories
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Celebrating Our Community's
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                Amazing Achievements
              </span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real stories from real people who achieved their learning goals with our tutors
            </p>
          </div>

          {/* Enhanced Success Stories Display */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl border border-white/50 p-8 md:p-12">
              <Suspense fallback={
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              }>
                <SuccessStoriesTicker subject="general" />
              </Suspense>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            {!user ? (
              <Button 
                size="lg"
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-4 px-8 rounded-full text-lg shadow-lg hover:shadow-xl transform transition-all duration-300 hover:scale-105"
                onClick={() => {
                  // Store intention to create success story after login
                  localStorage.setItem('returnToAction', 'create-success-story');
                  navigate('/auth');
                }}
              >
                <Trophy className="mr-2 h-5 w-5" />
                Start Your Success Story
                 <ArrowRight className="ml-2 h-5 w-5" />
               </Button>
             ) : (
               <Suspense fallback={
                 <Button 
                   size="lg"
                   disabled
                   className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold py-4 px-8 rounded-full text-lg"
                 >
                   <Trophy className="mr-2 h-5 w-5" />
                   Loading...
                 </Button>
               }>
                 <SuccessStoryForm 
                   trigger={
                     <Button 
                       size="lg"
                       className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-4 px-8 rounded-full text-lg shadow-lg hover:shadow-xl transform transition-all duration-300 hover:scale-105"
                     >
                       <Trophy className="mr-2 h-5 w-5" />
                       Start Your Success Story
                       <ArrowRight className="ml-2 h-5 w-5" />
                     </Button>
                   }
                 />
               </Suspense>
             )}
          </div>
        </div>
      </section>

      {/* Separator */}
      <div className="container mx-auto px-4 max-w-7xl">
        <Separator className="bg-gray-100 h-[2px]" />
      </div>

      {/* What Tutors Do Section - Enhanced with overlapping tutor photos */}
      <section className="py-16 lg:py-10 xl:py-16 w-full overflow-hidden bg-gradient-to-b from-white to-gray-50/50">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-12 lg:mb-8 xl:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-3xl xl:text-4xl font-bold text-gray-900 mb-4 lg:mb-3 xl:mb-4 break-words">Find the right tutor for you</h2>
            <p className="text-lg sm:text-xl lg:text-lg xl:text-xl text-gray-600 break-words">Experience personalized learning like never before</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 xl:gap-12 items-center max-w-6xl mx-auto mb-12 lg:mb-8 xl:mb-12">
            {/* Left side - Overlapping Tutor Photos */}
            <div className="flex justify-center lg:justify-end order-2 lg:order-1 pl-8 lg:pl-4 xl:pl-8">
              <div className="relative w-[500px] lg:w-[400px] xl:w-[500px] h-96 lg:h-80 xl:h-96">
                {/* First photo - Main photo */}
                 <div className="absolute top-0 left-0 w-80 lg:w-64 xl:w-80 h-80 lg:h-64 xl:h-80 rounded-2xl overflow-hidden shadow-xl z-10 border-4 border-white">
                  <img 
                    src={getLeftSectionImageUrl()}
                    alt="Professional tutor providing personalized lessons"
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                  />
                  
                  {/* View Profile Button */}
                  {leftImageTutor && (
                    <div className="absolute bottom-4 left-4 z-10">
                      <Button
                        size="sm"
                        className="bg-green-500 text-white font-semibold py-1.5 px-3 rounded-lg text-sm shadow-lg border border-green-300/60 flex items-center gap-1"
                        onClick={() => navigate(`/tutor/${leftImageTutor.id}`)}
                      >
                        <MessageCircle className="h-3 w-3" />
                        {leftImageTutor.name?.split(' ')[0] || 'Tutor'}
                      </Button>
                    </div>
                  )}
                </div>
                
                 {/* Second photo - Half size overlapping photo */}
                 <div className="absolute bottom-16 lg:bottom-12 xl:bottom-16 right-2 lg:right-6 xl:right-8 w-40 lg:w-32 xl:w-40 h-40 lg:h-32 xl:h-40 rounded-2xl overflow-hidden shadow-xl z-20 border-4 border-white">
                  <img 
                    src={getRightSectionImageUrl()}
                    alt="Student learning with tutor guidance | طالب يتعلم مع إرشاد المدرس"
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                  />
                  
                  {/* View Profile Button */}
                  {rightImageTutor && (
                    <div className="absolute bottom-2 right-2 z-10">
                      <Button
                        size="sm"
                        className="bg-green-500 text-white font-semibold py-1 px-2 rounded-lg text-xs shadow-lg border border-green-300/60 flex items-center gap-1"
                        onClick={() => navigate(`/tutor/${rightImageTutor.id}`)}
                      >
                        <MessageCircle className="h-2 w-2" />
                        {rightImageTutor.name?.split(' ')[0] || 'Tutor'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right side - Feature Cards */}
            <div className="grid grid-cols-1 gap-8 order-1 lg:order-2">
              <Card className="text-center border-0 shadow-md w-full">
                <CardContent className="p-6 sm:p-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Target className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 break-words">Tutors match your learning style</h3>
                  <p className="text-gray-600 text-sm sm:text-base break-words">Get matched with tutors who understand how you learn best and adapt their teaching methods accordingly.</p>
                </CardContent>
              </Card>

              <Card className="text-center border-0 shadow-md hover:shadow-lg transition-shadow w-full">
                <CardContent className="p-6 sm:p-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <BookOpen className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 break-words">Lessons tailored to your goals</h3>
                  <p className="text-gray-600 text-sm sm:text-base break-words">Every lesson is customized to help you achieve your specific learning objectives and milestones.</p>
                </CardContent>
              </Card>

            </div>
          </div>
        </div>
      </section>

      {/* Separator */}
      <div className="container mx-auto px-4 max-w-7xl">
        <Separator className="bg-gray-100 h-[2px]" />
      </div>

      {/* Featured Tutors Section - Updated to only show featured tutors */}
      <section id="featured-tutors" className="py-16 lg:py-12 xl:py-16 bg-gradient-to-b from-white to-gray-50/50">
        <div className="container mx-auto px-4">
          <h2 className="text-xl sm:text-3xl lg:text-2xl xl:text-3xl font-bold text-center text-gray-900 mb-8 lg:mb-6 xl:mb-12">Meet Our Top Tutors</h2>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 lg:py-8 xl:py-12 w-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600 text-center">Loading featured tutors...</p>
            </div>
          ) : featuredTutors.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-4 xl:gap-6">
              {featuredTutors.slice(0, 4).map((tutor) => (
                <Card 
                  key={tutor.id} 
                  className="group hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-200 bg-white relative overflow-hidden h-full"
                  onClick={() => handleTutorClick(tutor)}
                >
                  
                  <CardContent className="p-4 lg:p-3 xl:p-4 relative h-full flex flex-col">
                     {/* Rating and Featured badges */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                        <TutorRating 
                          rating={reviewStats[tutor.id]?.average || 5.0} 
                          count={reviewStats[tutor.id]?.count || 0} 
                        />
                      </div>
                      <div className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
                        Featured
                      </div>
                    </div>
                    
                    <div className="text-center flex-1 flex flex-col">
                      {/* Avatar */}
                       <div className="relative mb-3 lg:mb-2 xl:mb-3">
                        <Avatar className="h-20 w-20 lg:h-16 lg:w-16 xl:h-20 xl:w-20 mx-auto border-2 border-gray-100">
                          <AvatarImage src={getProfilePictureUrl(tutor)} alt={getTutorName(tutor)} className="object-cover" loading="lazy" />
                          <AvatarFallback className="bg-gray-100 text-gray-700 text-lg lg:text-base xl:text-lg font-bold">
                            {getUserInitials(getTutorName(tutor))}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      
                      {/* Name */}
                      <h3 className="font-semibold text-base lg:text-sm xl:text-base text-gray-900 mb-2 lg:mb-1 xl:mb-2">{getTutorName(tutor)}</h3>
                      
                      {/* Country Flag */}
                      <div className="flex items-center justify-center mb-2 lg:mb-1 xl:mb-2">
                        {tutor.country && tutor.country.trim() !== '' ? (
                          <TutorCountryFlag 
                            countryCode={tutor.country} 
                            showName={true}
                            size="sm"
                            className="text-xs text-gray-600"
                          />
                        ) : null}
                      </div>
                      
                      {/* Subjects */}
                      <div className="text-xs text-gray-600 mb-2">
                        <span className="line-clamp-2">
                          {tutor.subjects?.slice(0, 2).join(', ')}
                          {tutor.subjects?.length > 2 && ` +${tutor.subjects.length - 2} more`}
                        </span>
                      </div>
                      
                       {/* Price */}
                       <div className="mb-2 lg:mb-1 xl:mb-2">
                         {selectedCurrency.code === 'EGP' ? (
                           <div className="flex items-baseline gap-1 justify-center mb-1" dir="rtl">
                             <span className="text-lg lg:text-base xl:text-lg font-bold text-gray-900">
                               {formatPrice(tutor.hourlyRate || 0).replace(selectedCurrency.symbol, '').trim()}
                             </span>
                             <span className="text-base lg:text-sm xl:text-base font-semibold text-gray-900">{selectedCurrency.symbol}</span>
                             <span className="text-xs font-normal text-gray-500">/ ساعة</span>
                           </div>
                         ) : (
                           <div className="text-lg lg:text-base xl:text-lg font-bold text-gray-900 mb-1">
                             {formatTutorPrice(tutor)}
                             <span className="text-xs font-normal text-gray-500">/hr</span>
                           </div>
                         )}
                         <Button
                           variant="ghost"
                           size="sm"
                           className="h-6 px-2 text-xs"
                           onClick={(e) => {
                             e.stopPropagation();
                             setSelectedTutorForPricing(tutor);
                             setShowPricingModal(true);
                           }}
                         >
                           <Info className="h-3 w-3 mr-1" />
                           {selectedCurrency.code === 'EGP' ? 'خيارات أخرى للأسعار' : 'More Options'}
                         </Button>
                       </div>
                       
                       {/* Session Preference - Highlighted for importance */}
                       <div className="mb-3 lg:mb-2 xl:mb-3">
                         {(() => {
                           const sessionInfo = getSessionPreferenceInfo(tutor.sessionPreference);
                           return (
                             <div className="inline-flex items-center gap-1 px-3 lg:px-2 xl:px-3 py-1.5 lg:py-1 xl:py-1.5 rounded-full text-xs lg:text-xs xl:text-xs bg-gradient-to-r from-purple-100 to-blue-100 text-purple-800 font-semibold border border-purple-200">
                               <sessionInfo.icon className="h-3 w-3" />
                               {sessionInfo.text}
                             </div>
                           );
                         })()}
                        </div>
                         
                         {/* Action buttons */}
                         <div className="space-y-2 mt-auto">
                           {/* Payment Methods Button - Always at bottom */}
                           <Button
                             variant="outline"
                             size="sm"
                             className="w-full text-xs h-7"
                             onClick={(e) => {
                               e.stopPropagation();
                               setSelectedTutorForPayment(tutor);
                               setShowPaymentModal(true);
                             }}
                           >
                             <Wallet className="h-3 w-3 mr-1" />
                             Payment Methods
                           </Button>

                           <Button
                          size="sm"
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium h-8 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBookSession(tutor);
                          }}
                        >
                          <Calendar className="h-3 w-3 mr-1" />
                          Book Session
                        </Button>
                        
                        <div className="grid grid-cols-3 gap-1">
                          <Button 
                            size="sm"
                            variant="outline"
                            className="text-xs px-2 py-1 h-6 text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewLessons(tutor);
                            }}
                          >
                            <BookOpen className="h-3 w-3" />
                          </Button>
                          
                          <Button 
                            size="sm"
                            variant="outline"
                            className="text-xs px-2 py-1 h-6 text-purple-600 border-purple-200 hover:bg-purple-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSendMessage(tutor);
                            }}
                          >
                            <MessageCircle className="h-3 w-3" />
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs px-2 py-1 h-6 text-red-600 border-red-200 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleSave(tutor);
                            }}
                            title={isTutorSaved(tutor.id) ? "Remove from saved" : "Save tutor"}
                          >
                            <Heart 
                              className={`h-3 w-3 ${
                                isTutorSaved(tutor.id) 
                                  ? 'text-red-500 fill-current' 
                                  : 'text-gray-400'
                              }`} 
                            />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">
                No featured tutors have been selected yet. Our admin team is working on curating the best tutors for you.
              </p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/tutors')}
                className="border-blue-200 text-blue-600 hover:bg-blue-50"
              >
                Browse All Tutors
              </Button>
            </div>
          )}
          <div className="text-center mt-8 lg:mt-6 xl:mt-8">
            <Button 
              variant="outline" 
              onClick={() => navigate('/tutors')}
              className="border-blue-200 text-blue-600 hover:bg-blue-50"
            >
              View All Tutors
            </Button>
          </div>
        </div>
      </section>

      {/* Separator */}
      <div className="container mx-auto px-4 max-w-7xl">
        <Separator className="bg-gray-100 h-[2px]" />
      </div>

      {/* How It Works Section - Enhanced with animations and illustrations */}
      <section id="how-it-works" className="py-16 lg:py-12 xl:py-20 bg-gradient-to-b from-white to-gray-50/50 w-full overflow-hidden relative">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200/10 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-purple-200/10 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-32 left-1/4 w-40 h-40 bg-green-200/10 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-20 right-1/3 w-20 h-20 bg-teal-200/10 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
        </div>

        <div className="container mx-auto px-4 max-w-7xl relative z-10">
          <div className="text-center mb-6 sm:mb-12 lg:mb-8 xl:mb-16 animate-fade-in">
            <h2 className="text-xl sm:text-3xl md:text-4xl lg:text-3xl xl:text-4xl font-bold text-gray-900 mb-2 sm:mb-6 lg:mb-3 xl:mb-6 break-words">How the platform works</h2>
            <p className="text-sm sm:text-xl md:text-2xl lg:text-lg xl:text-2xl text-gray-600 max-w-3xl mx-auto break-words">Start learning in just a few simple steps</p>
            <div className="w-12 sm:w-24 lg:w-16 xl:w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mt-2 sm:mt-6 lg:mt-3 xl:mt-6 rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-8 md:gap-12 lg:gap-6 xl:gap-12 max-w-7xl mx-auto mb-12 lg:mb-8 xl:mb-16">
            {/* Step 1 - Find your tutor */}
            <div className="text-center w-full group animate-fade-in" style={{animationDelay: '0.2s'}}>
              <div className="relative mb-2 sm:mb-8 lg:mb-4 xl:mb-8">
                <div className="w-16 h-16 sm:w-32 sm:h-32 lg:w-24 lg:h-24 xl:w-32 xl:h-32 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-6 lg:mb-3 xl:mb-6 text-white font-bold text-lg sm:text-3xl lg:text-2xl xl:text-3xl shadow-2xl group-hover:scale-110 transition-all duration-500 relative overflow-hidden animate-pulse" style={{
                  animation: 'pulse 2s ease-in-out infinite'
                }}>
                  <span className="relative z-10">1</span>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-full"></div>
                </div>   
              </div>
              
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 sm:p-6 lg:p-4 xl:p-6 shadow-lg group-hover:shadow-xl transition-all duration-300 border border-blue-100/50">
                <div className="w-10 h-10 sm:w-16 sm:h-16 lg:w-12 lg:h-12 xl:w-16 xl:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-6 lg:mb-3 xl:mb-6 group-hover:bg-blue-200 transition-colors duration-300" style={{
                  animation: 'pulse 2s ease-in-out infinite'
                }}>
                  <Search className="h-5 w-5 sm:h-8 sm:w-8 lg:h-6 lg:w-6 xl:h-8 xl:w-8 text-blue-600 group-hover:scale-110 transition-transform duration-300" />
                </div>
                <h3 className="text-sm sm:text-lg md:text-xl lg:text-lg xl:text-xl font-bold text-gray-900 mb-1 sm:mb-4 lg:mb-2 xl:mb-4 break-words">Find your tutor</h3>
                <p className="text-gray-600 text-xs sm:text-sm md:text-base lg:text-sm xl:text-base break-words leading-relaxed">Browse through verified profiles, read reviews, and find the perfect tutor that matches your learning style and goals</p>
              </div>
            </div>

            {/* Step 2 - Book a lesson */}
            <div className="text-center w-full group animate-fade-in" style={{animationDelay: '0.4s'}}>
              <div className="relative mb-2 sm:mb-8 lg:mb-4 xl:mb-8">
                <div className="w-16 h-16 sm:w-32 sm:h-32 lg:w-24 lg:h-24 xl:w-32 xl:h-32 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-6 lg:mb-3 xl:mb-6 text-white font-bold text-lg sm:text-3xl lg:text-2xl xl:text-3xl shadow-2xl group-hover:scale-110 transition-all duration-500 relative overflow-hidden" style={{
                  animation: 'pulse 2s ease-in-out infinite 0.5s'
                }}>
                  <span className="relative z-10">2</span>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-full"></div>
                </div>      
              </div>
              
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 sm:p-6 lg:p-4 xl:p-6 shadow-lg group-hover:shadow-xl transition-all duration-300 border border-green-100/50">
                <div className="w-10 h-10 sm:w-16 sm:h-16 lg:w-12 lg:h-12 xl:w-16 xl:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-6 lg:mb-3 xl:mb-6 group-hover:bg-green-200 transition-colors duration-300" style={{
                  animation: 'pulse 2s ease-in-out infinite 0.5s'
                }}>
                  <Calendar className="h-5 w-5 sm:h-8 sm:w-8 lg:h-6 lg:w-6 xl:h-8 xl:w-8 text-green-600 group-hover:scale-110 transition-transform duration-300" />
                </div>
                <h3 className="text-sm sm:text-lg md:text-xl lg:text-lg xl:text-xl font-semibold text-gray-900 mb-1 sm:mb-4 lg:mb-2 xl:mb-4 break-words">Book a lesson</h3>
                <p className="text-gray-600 text-xs sm:text-sm md:text-base lg:text-sm xl:text-base break-words leading-relaxed">Schedule your first lesson at a convenient time. Choose from available slots and get instant confirmation</p>
              </div>
            </div>

            {/* Step 3 - Start learning */}
            <div className="text-center w-full group animate-fade-in" style={{animationDelay: '0.6s'}}>
              <div className="relative mb-2 sm:mb-8 lg:mb-4 xl:mb-8">
                <div className="w-16 h-16 sm:w-32 sm:h-32 lg:w-24 lg:h-24 xl:w-32 xl:h-32 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-6 lg:mb-3 xl:mb-6 text-white font-bold text-lg sm:text-3xl lg:text-2xl xl:text-3xl shadow-2xl group-hover:scale-110 transition-all duration-500 relative overflow-hidden" style={{
                  animation: 'pulse 2s ease-in-out infinite 1s'
                }}>
                  <span className="relative z-10">3</span>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-full"></div>
                </div>        
              </div>
              
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 sm:p-6 lg:p-4 xl:p-6 shadow-lg group-hover:shadow-xl transition-all duration-300 border border-purple-100/50">
                <div className="w-10 h-10 sm:w-16 sm:h-16 lg:w-12 lg:h-12 xl:w-16 xl:h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-6 lg:mb-3 xl:mb-6 group-hover:bg-purple-200 transition-colors duration-300" style={{
                  animation: 'pulse 2s ease-in-out infinite 1s'
                }}>
                  <Play className="h-5 w-5 sm:h-8 sm:w-8 lg:h-6 lg:w-6 xl:h-8 xl:w-8 text-purple-600 group-hover:scale-110 transition-transform duration-300" />
                </div>
                <h3 className="text-sm sm:text-lg md:text-xl lg:text-lg xl:text-xl font-bold text-gray-900 mb-1 sm:mb-4 lg:mb-2 xl:mb-4 break-words">Start learning</h3>
                <p className="text-gray-600 text-xs sm:text-sm md:text-base lg:text-sm xl:text-base break-words leading-relaxed">Join your virtual classroom with HD video, interactive whiteboard, and personalized lesson materials</p>
              </div>
            </div>

            {/* Step 4 - Improve over time */}
            <div className="text-center w-full group animate-fade-in" style={{animationDelay: '0.8s'}}>
              <div className="relative mb-2 sm:mb-8 lg:mb-4 xl:mb-8">
                <div className="w-16 h-16 sm:w-32 sm:h-32 lg:w-24 lg:h-24 xl:w-32 xl:h-32 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-6 lg:mb-3 xl:mb-6 text-white font-bold text-lg sm:text-3xl lg:text-2xl xl:text-3xl shadow-2xl group-hover:scale-110 transition-all duration-500 relative overflow-hidden" style={{
                  animation: 'pulse 2s ease-in-out infinite 1.5s'
                }}>
                  <span className="relative z-10">4</span>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-full"></div>
                </div>
              </div>
              
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 sm:p-6 lg:p-4 xl:p-6 shadow-lg group-hover:shadow-xl transition-all duration-300 border border-orange-100/50">
                <div className="w-10 h-10 sm:w-16 sm:h-16 lg:w-12 lg:h-12 xl:w-16 xl:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-6 lg:mb-3 xl:mb-6 group-hover:bg-orange-200 transition-colors duration-300" style={{
                  animation: 'pulse 2s ease-in-out infinite 1.5s'
                }}>
                  <TrendingUp className="h-5 w-5 sm:h-8 sm:w-8 lg:h-6 lg:w-6 xl:h-8 xl:w-8 text-orange-600 group-hover:scale-110 transition-transform duration-300" />
                </div>
                <h3 className="text-sm sm:text-lg md:text-xl lg:text-lg xl:text-xl font-semibold text-gray-900 mb-1 sm:mb-4 lg:mb-2 xl:mb-4 break-words">Improve over time</h3>
                <p className="text-gray-600 text-xs sm:text-sm md:text-base lg:text-sm xl:text-base break-words leading-relaxed">Track your progress with detailed analytics and celebrate your achievements as you reach your goals</p>
              </div>
            </div>
          </div>

          {/* Animated Process Flow Diagram */}
          <div className="relative max-w-4xl mx-auto mb-12 animate-fade-in" style={{animationDelay: '1s'}}>
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-gray-100">
              <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">Your Learning Journey</h3>
              
              {/* Flow diagram with animated connections */}
              <div className="relative">
                <svg className="w-full h-32 mb-6" viewBox="0 0 800 120" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#3B82F6" />
                      <stop offset="33%" stopColor="#10B981" />
                      <stop offset="66%" stopColor="#8B5CF6" />
                      <stop offset="100%" stopColor="#F59E0B" />
                    </linearGradient>
                  </defs>
                  
                  {/* Animated flowing line */}
                  <path 
                    d="M 50 60 Q 200 30 350 60 Q 500 90 650 60 Q 700 50 750 60" 
                    stroke="url(#flowGradient)" 
                    strokeWidth="4" 
                    fill="none"
                    className="animate-pulse"
                  />
                  
                  {/* Animated dots along the path */}
                  <circle cx="50" cy="60" r="8" fill="#3B82F6" className="animate-pulse">
                    <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="275" cy="45" r="8" fill="#10B981" className="animate-pulse" style={{animationDelay: '0.5s'}}>
                    <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" begin="0.5s" />
                  </circle>
                  <circle cx="525" cy="75" r="8" fill="#8B5CF6" className="animate-pulse" style={{animationDelay: '1s'}}>
                    <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" begin="1s" />
                  </circle>
                  <circle cx="750" cy="60" r="8" fill="#F59E0B" className="animate-pulse" style={{animationDelay: '1.5s'}}>
                    <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" begin="1.5s" />
                  </circle>
                </svg>
                
                {/* Progress indicators */}
                <div className="flex justify-between items-center text-sm text-gray-600">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
                    <span>Discovery</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2 animate-pulse" style={{animationDelay: '0.5s'}}></div>
                    <span>Booking</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-2 animate-pulse" style={{animationDelay: '1s'}}></div>
                    <span>Learning</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-2 animate-pulse" style={{animationDelay: '1.5s'}}></div>
                    <span>Growth</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center animate-fade-in" style={{animationDelay: '1.2s'}}>
            <Button 
              size="lg"
              onClick={() => navigate('/tutors')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-4 px-12 rounded-full text-lg shadow-xl hover:shadow-2xl transform transition-all duration-300 hover:scale-105"
            >
              Start Your Journey
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Separator */}
      <div className="container mx-auto px-4 max-w-7xl">
        <Separator className="bg-gray-100 h-[2px]" />
      </div>

      {/* Become a Tutor Section - Changed background color */}
      <section id="become-tutor" className="py-16 bg-gradient-to-b from-white to-gray-50/50 w-full overflow-hidden">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 break-words">Become a Tutor</h2>
            <p className="text-lg sm:text-xl mb-8 break-words">Share your knowledge and earn money doing what you love</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="flex items-center space-x-3 justify-center md:justify-start">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-base sm:text-lg break-words">Flexible hours</span>
              </div>
              <div className="flex items-center space-x-3 justify-center md:justify-start">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-base sm:text-lg break-words">Earn doing what you love</span>
              </div>
              <div className="flex items-center space-x-3 justify-center md:justify-start">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-base sm:text-lg break-words">Join a global teaching community</span>
              </div>
            </div>
            
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-full text-lg w-full sm:w-auto"
              onClick={() => {
                navigate('/auth');
                // Set the tab to register after navigation
                setTimeout(() => {
                  const registerTab = document.querySelector('[value="register"]') as HTMLElement;
                  if (registerTab) {
                    registerTab.click();
                  }
                }, 100);
              }}
            >
              Start Teaching
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      <BookingModal
        isOpen={!!selectedTutorForBooking}
        onClose={() => setSelectedTutorForBooking(null)}
        tutor={selectedTutorForBooking}
      />

      <SendMessageModal
        isOpen={!!selectedTutorForMessage}
        onClose={() => setSelectedTutorForMessage(null)}
        tutor={selectedTutorForMessage}
      />

      {/* Hidden SuccessStoryForm for programmatic triggering */}
      <Suspense fallback={null}>
        <SuccessStoryForm 
          openModal={triggerSuccessStoryModal}
          onModalOpenChange={(open) => {
            if (!open) setTriggerSuccessStoryModal(false);
          }}
          trigger={<div style={{ display: 'none' }}></div>}
        />
      </Suspense>

      {/* Subjects Modal */}
      <SubjectsModal 
        isOpen={isSubjectsModalOpen}
        onClose={() => setIsSubjectsModalOpen(false)}
      />

      {/* Payment Methods Modal */}
      <PaymentMethodsModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        tutorName={selectedTutorForPayment ? getTutorName(selectedTutorForPayment) : ''}
      />

      {/* Tutor Pricing Modal */}
      <TutorPricingModal
        isOpen={showPricingModal}
        onClose={() => {
          setShowPricingModal(false);
          setSelectedTutorForPricing(null);
        }}
        tutor={selectedTutorForPricing}
      />

      </div> {/* Close the pt-[60px] div */}
    </div>
  );
};

export default Home;
